import { ref, readonly } from "vue";
import { io, Socket } from "socket.io-client";
import { useAuthStore } from "@/stores/auth";

let socket: Socket | null = null;
const connected = ref(false);
const _disconnectedAt = ref<number | null>(null);
const _reconnectCallbacks: Array<() => void> = [];

/*
 * Reconexão depois que o SERVIDOR fecha a conexão ("io server disconnect").
 * O socket.io não reconecta sozinho nesse caso, então é feita à mão — com
 * espera crescente e com limite, porque a primeira versão tentava de novo na
 * hora e sem teto: se o gateway recusasse até o token recém-renovado, a página
 * ficava renovando token e refazendo handshake sem parar (achado do Codex).
 */
const INITIAL_RETRY_DELAY_MS = 1000;
const MAX_RETRY_DELAY_MS = 30000;
/**
 * Recusas SEGUIDAS com o token acabado de renovar. Se nem o token novo passa,
 * o problema não é o token — insistir só martela o /auth/refresh-token.
 */
const MAX_FRESH_TOKEN_REJECTIONS = 3;
/**
 * O gateway autentica DEPOIS do `connect` e derruba logo em seguida quando
 * recusa. Só uma conexão que sobrevive a isto conta como aceita — zerar os
 * contadores já no `connect` recriaria o laço.
 */
const STABLE_CONNECTION_MS = 10000;
/**
 * Pelo mesmo motivo, o aviso de "voltei da queda" (que recarrega TODAS as
 * telas abertas) espera a conexão se confirmar. Disparar já no `connect` fazia
 * cada reconexão recusada recarregar tudo à toa — e o caminho normal de token
 * vencido (conecta com o velho, é recusado, conecta com o novo) recarregava
 * duas vezes (achado do code-reviewer). Curto de propósito: é quanto o dado
 * fresco atrasa depois de uma queda.
 */
const CONFIRMATION_MS = 1500;

let attempts = 0;
let freshTokenRejections = 0;
let justRefreshed = false;
let scheduledReconnect: ReturnType<typeof setTimeout> | null = null;
let stabilityTimer: ReturnType<typeof setTimeout> | null = null;
let confirmationTimer: ReturnType<typeof setTimeout> | null = null;

function resetReconnect() {
	if (scheduledReconnect) clearTimeout(scheduledReconnect);
	if (stabilityTimer) clearTimeout(stabilityTimer);
	if (confirmationTimer) clearTimeout(confirmationTimer);
	scheduledReconnect = null;
	stabilityTimer = null;
	confirmationTimer = null;
	attempts = 0;
	freshTokenRejections = 0;
	justRefreshed = false;
}

function scheduleReconnect() {
	if (scheduledReconnect || !socket) return;
	const delay = Math.min(INITIAL_RETRY_DELAY_MS * 2 ** attempts, MAX_RETRY_DELAY_MS);
	attempts++;
	scheduledReconnect = setTimeout(async () => {
		scheduledReconnect = null;
		const instance = socket;
		if (!instance) return;
		const refreshedToken = await useAuthStore().refreshAccessToken();
		// Durante a renovação a aba pode ter saído e voltado: `disconnect()` +
		// `connect()` criam OUTRO socket. O resultado desta tentativa é da
		// instância velha — aplicar no socket novo agendaria renovação à toa com a
		// conexão já saudável, ou mexeria no socket que não pediu (achado do Codex).
		if (socket !== instance) return;
		if (!refreshedToken) {
			// O store só desloga em 401/403 (refresh inválido): aí o token some e
			// não há o que retomar. Timeout/5xx mantém a sessão — é o backend
			// reiniciando —, então tenta de novo mais tarde. Desistir aqui deixava
			// a aba sem avisos até o F5, mesmo com o backend já de volta.
			if (useAuthStore().token) scheduleReconnect();
			return;
		}
		justRefreshed = true;
		instance.auth = { token: useAuthStore().token };
		instance.connect();
	}, delay);
}

function getSocket(): Socket {
	if (socket) return socket;

	const auth = useAuthStore();
	socket = io("/ws", {
		auth: { token: auth.token },
		transports: ["websocket"],
		reconnection: true,
		reconnectionAttempts: Infinity,
		reconnectionDelay: 1000,
		reconnectionDelayMax: 10000,
	});

	socket.io.on("reconnect_attempt", () => {
		const freshToken = useAuthStore().token;
		if (socket) {
			socket.auth = { token: freshToken };
		}
	});

	socket.on("connect", () => {
		connected.value = true;

		// Voltando de uma queda? Então quem depende de dado fresco precisa
		// recarregar: o que mudou no terreiro enquanto o socket estava fora não
		// gerou evento nenhum para esta aba (FEAT-044). Só depois de a conexão se
		// confirmar — ver CONFIRMATION_MS.
		if (_disconnectedAt.value !== null) {
			if (confirmationTimer) clearTimeout(confirmationTimer);
			confirmationTimer = setTimeout(() => {
				confirmationTimer = null;
				// A hora da queda só é apagada DEPOIS dos callbacks: quem pergunta
				// `wasDownLong()` lá dentro precisa dela. Apagando antes, a resposta era
				// sempre "não" e o aviso de "reconectado" da chamada nunca aparecia.
				_reconnectCallbacks.forEach((cb) => cb());
				_disconnectedAt.value = null;
			}, CONFIRMATION_MS);
		}

		if (stabilityTimer) clearTimeout(stabilityTimer);
		stabilityTimer = setTimeout(() => {
			stabilityTimer = null;
			attempts = 0;
			freshTokenRejections = 0;
			justRefreshed = false;
		}, STABLE_CONNECTION_MS);
	});

	socket.on("connect_error", async (err: Error) => {
		if (err.message?.includes("Auth failed") || err.message?.includes("jwt expired")) {
			const refreshed = await useAuthStore().refreshAccessToken();
			if (refreshed && socket) {
				socket.auth = { token: useAuthStore().token };
			}
		}
	});

	socket.on("disconnect", (reason: string) => {
		connected.value = false;
		// Guarda a hora da PRIMEIRA queda: uma reconexão recusada no meio do caminho
		// não pode encurtar o tempo que a aba ficou sem avisos.
		if (_disconnectedAt.value === null) _disconnectedAt.value = Date.now();
		if (stabilityTimer) {
			clearTimeout(stabilityTimer);
			stabilityTimer = null;
		}
		// Caiu antes de se confirmar: a volta não aconteceu, nada de recarregar.
		if (confirmationTimer) {
			clearTimeout(confirmationTimer);
			confirmationTimer = null;
		}

		// "io server disconnect" é o servidor fechando a conexão — no ORI, é o
		// gateway recusando o handshake com token vencido. Nesse caso o socket.io
		// NÃO reconecta sozinho (ao contrário da queda de rede), e a página ficava
		// sem avisos de cadastro até alguém recarregar.
		if (reason !== "io server disconnect") return;
		if (justRefreshed) freshTokenRejections++;
		justRefreshed = false;
		if (freshTokenRejections >= MAX_FRESH_TOKEN_REJECTIONS) return;
		scheduleReconnect();
	});

	return socket;
}

function connect() {
	getSocket().connect();
}

function disconnect() {
	// Logout ou saída do layout: reconexão agendada não pode ressuscitar o socket.
	resetReconnect();
	if (socket) {
		socket.disconnect();
		socket = null;
		connected.value = false;
	}
	// Sair de propósito não é queda: o handler de `disconnect` marcou a hora, e
	// sem limpar a próxima conexão (novo login, layout remontado) dispararia
	// a recarga de "voltei da queda" à toa.
	_disconnectedAt.value = null;
}

function joinRoom(room: string) {
	getSocket().emit("joinRitual", { ritualId: room });
}

function leaveRoom(room: string) {
	getSocket().emit("leaveRitual", { ritualId: room });
}

function on(event: string, handler: (...args: any[]) => void) {
	getSocket().on(event, handler);
}

function off(event: string, handler: (...args: any[]) => void) {
	getSocket().off(event, handler);
}

function onReconnect(cb: () => void) {
	_reconnectCallbacks.push(cb);
}

function offReconnect(cb: () => void) {
	const idx = _reconnectCallbacks.indexOf(cb);
	if (idx >= 0) _reconnectCallbacks.splice(idx, 1);
}

function wasDownLong(): boolean {
	return !!_disconnectedAt.value && Date.now() - _disconnectedAt.value > 5000;
}

export const socketService = {
	connect,
	disconnect,
	joinRoom,
	leaveRoom,
	on,
	off,
	onReconnect,
	offReconnect,
	wasDownLong,
	connected: readonly(connected),
};
